import { Home, Activity, Wallet, User, QrCode, type LucideIcon } from 'lucide-react';
import { useNotificationStore } from '../../store/notificationStore';

/**
 * The app's bottom navigation.
 *
 * Two variants exist — this one and BottomNavCharging — so the pieces they share
 * (the tab list, a tab button, the raised scan button) live here and are imported
 * there. They had already drifted apart once: this bar carried five tabs and an
 * unread badge while the charging bar carried four and no badge at all.
 */

export type NavTab = 'map' | 'activity' | 'wallet' | 'notifications' | 'profile';

interface NavTabDef {
  id: NavTab;
  label: string;
  icon: LucideIcon;
}

/**
 * Scan sits in the middle, so the tabs are declared as the two halves that
 * flank it. Messages is deliberately absent: it lives on the Profile tab now,
 * and its unread badge rides the Profile icon.
 */
export const NAV_TABS_LEFT: NavTabDef[] = [
  { id: 'map', label: 'Home', icon: Home },
  { id: 'activity', label: 'Activity', icon: Activity },
];

export const NAV_TABS_RIGHT: NavTabDef[] = [
  { id: 'wallet', label: 'Wallet', icon: Wallet },
  { id: 'profile', label: 'Profile', icon: User },
];

/** The green bar and the charging bar tint their contents differently. */
export type NavTone = 'default' | 'charging';

const toneClasses = {
  default: {
    active: 'text-[#8bc43e]',
    idle: 'text-gray-400 dark:text-gray-500',
    scanRing: 'ring-white dark:ring-gray-800',
    scanFace: 'bg-gradient-to-br from-[#8bc43e] to-[#6fa02e]',
    scanIcon: 'text-white',
    scanLabel: 'text-[#8bc43e]',
  },
  charging: {
    active: 'text-white',
    idle: 'text-white/60',
    scanRing: 'ring-[#7ab233]',
    scanFace: 'bg-white',
    scanIcon: 'text-[#6fa02e]',
    scanLabel: 'text-white',
  },
} as const;

export function NavTabButton({
  tab,
  isActive,
  onClick,
  badge = 0,
  tone = 'default',
}: {
  tab: NavTabDef;
  isActive: boolean;
  onClick: () => void;
  badge?: number;
  tone?: NavTone;
}) {
  const Icon = tab.icon;
  const t = toneClasses[tone];

  return (
    <button
      onClick={onClick}
      aria-current={isActive ? 'page' : undefined}
      className="flex-1 flex flex-col items-center justify-end py-2 px-1 relative transition-all duration-200 active:scale-95"
    >
      <div className="relative">
        <Icon
          className={`w-6 h-6 transition-colors duration-200 ${isActive ? t.active : t.idle}`}
          strokeWidth={isActive ? 2.5 : 2}
        />
        {badge > 0 && (
          <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center leading-none">
            {badge > 99 ? '99+' : badge}
          </span>
        )}
      </div>
      <span
        className={`text-xs mt-1 font-medium transition-colors duration-200 ${
          isActive ? t.active : t.idle
        }`}
      >
        {tab.label}
      </span>
    </button>
  );
}

/**
 * Scanning is a verb, not a destination, so it is a raised button rather than a
 * sixth tab — it opens an overlay over whatever tab you are on and has no active
 * state of its own. The circle is absolutely positioned so it can break above the
 * bar without shortening the row or pulling the labels out of line.
 */
export function NavScanButton({
  onClick,
  tone = 'default',
}: {
  onClick: () => void;
  tone?: NavTone;
}) {
  const t = toneClasses[tone];

  return (
    <button
      onClick={onClick}
      aria-label="Scan QR code"
      className="flex-1 flex flex-col items-center justify-end py-2 px-1 relative transition-transform duration-200 active:scale-95"
    >
      <span
        className={`absolute -top-7 left-1/2 -translate-x-1/2 w-14 h-14 rounded-full flex items-center justify-center shadow-lg ring-4 ${t.scanFace} ${t.scanRing}`}
      >
        <QrCode className={`w-7 h-7 ${t.scanIcon}`} aria-hidden="true" />
      </span>
      <span className={`text-xs mt-1 font-semibold ${t.scanLabel}`}>Scan</span>
    </button>
  );
}

interface BottomNavProps {
  activeTab: NavTab;
  onTabChange: (tab: NavTab) => void;
  onScan: () => void;
  hasActiveSession?: boolean;
}

export function BottomNav({ activeTab, onTabChange, onScan }: BottomNavProps) {
  const unreadCount = useNotificationStore((s) => s.unreadCount);

  return (
    <nav className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 shadow-[0_-4px_20px_rgba(0,0,0,0.08)] dark:shadow-[0_-4px_20px_rgba(0,0,0,0.3)] flex-shrink-0 transition-colors relative z-[1000] pb-[env(safe-area-inset-bottom)]">
      <div className="flex items-stretch justify-around px-2 py-2">
        {NAV_TABS_LEFT.map((tab) => (
          <NavTabButton
            key={tab.id}
            tab={tab}
            isActive={activeTab === tab.id}
            onClick={() => onTabChange(tab.id)}
          />
        ))}

        <NavScanButton onClick={onScan} />

        {NAV_TABS_RIGHT.map((tab) => (
          <NavTabButton
            key={tab.id}
            tab={tab}
            isActive={activeTab === tab.id || (tab.id === 'profile' && activeTab === 'notifications')}
            onClick={() => onTabChange(tab.id)}
            badge={tab.id === 'profile' ? unreadCount : 0}
          />
        ))}
      </div>
    </nav>
  );
}
